import { NavLink, Outlet, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { EXTERNAL_LINKS } from '@reflex/shared/external-links';
import { cn } from '@reflex/ui/lib/utils';
import { SectionLabel } from '@reflex/ui/components/layout/Typography';
import { ReflexMark } from '@reflex/ui/components/brand/ReflexMark';
import { marketingApi, marketingQueryKeys } from './api';
import { resolveIcon } from './icons';

/**
 * Standalone layout for un-authed marketing pages served under `/pages/*`.
 *
 * Renders outside `AuthGate`/`AppShell`: has its own slim top bar, a left
 * "Plugins" subnav listing every plugin-contributed marketing page, a main
 * content area (`<Outlet />`) for the active page, and a footer.
 *
 * The subnav is built from each enabled plugin manifest's `web.routes[*].marketing`
 * metadata so that any future plugin with a public route automatically shows
 * up without changes here.
 *
 * Embed mode: when loaded with `?embed=1` in the URL, the header, left
 * subnav, and footer are suppressed. This lets the authed `/plugins/marketing`
 * hub embed a live, chrome-free preview of each marketing page in an iframe
 * without the layout's own navigation leaking into the preview.
 */
export function MarketingLayout() {
  const { data: pages = [] } = useQuery({
    queryKey: marketingQueryKeys.published(),
    queryFn: () => marketingApi.listPublished(),
    staleTime: 30_000,
  });
  const marketingPages = pages
    .filter((p) => p.slug !== 'overview')
    .map((p) => ({
      path: `/pages/${p.slug}`,
      label: p.title,
      icon: resolveIcon(p.icon),
    }));
  const [searchParams] = useSearchParams();
  const embed = searchParams.get('embed') === '1';

  if (embed) {
    return (
      <div className="flex min-h-dvh flex-col bg-background text-foreground">
        <main className="mx-auto w-full max-w-5xl flex-1 px-4 py-6 md:px-6 md:py-10">
          <Outlet />
        </main>
      </div>
    );
  }

  return (
    <div className="flex min-h-dvh flex-col bg-background text-foreground">
      <header className="sticky top-0 z-20 flex h-14 items-center justify-between border-b border-border/60 bg-background/80 px-4 backdrop-blur md:px-6">
        <NavLink to="/pages" className="flex items-center gap-2 font-semibold tracking-tight">
          <ReflexMark variant="tile" className="h-7 w-7" />
          <span>Reflex</span>
        </NavLink>
        <nav className="flex items-center gap-2 text-sm">
          <a
            href="https://github.com/runloopai"
            target="_blank"
            rel="noopener noreferrer"
            className="hidden rounded-md px-3 py-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground sm:inline-flex"
          >
            GitHub
          </a>
          <NavLink
            to="/"
            className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Sign in
          </NavLink>
        </nav>
      </header>

      <div className="mx-auto flex w-full max-w-7xl flex-1 flex-col gap-6 px-4 py-6 md:flex-row md:px-6 md:py-10">
        <aside className="shrink-0 md:w-56">
          <div className="sticky top-20 flex flex-col gap-2">
            <SectionLabel>Plugins</SectionLabel>
            <ul className="flex flex-col gap-0.5" data-testid="marketing-subnav">
              {marketingPages.length === 0 && (
                <li className="px-2 py-1.5 text-sm text-muted-foreground">No pages available.</li>
              )}
              {marketingPages.map((page) => {
                const Icon = page.icon;
                return (
                  <li key={page.path}>
                    <NavLink
                      to={page.path}
                      end
                      className={({ isActive }) =>
                        cn(
                          'flex items-center gap-2 rounded-md px-2 py-1.5 text-sm transition-colors',
                          isActive
                            ? 'bg-muted font-medium text-foreground'
                            : 'text-muted-foreground hover:bg-muted/60 hover:text-foreground',
                        )
                      }
                    >
                      <Icon className="h-4 w-4" />
                      <span className="truncate">{page.label}</span>
                    </NavLink>
                  </li>
                );
              })}
            </ul>
          </div>
        </aside>

        <main className="min-w-0 flex-1">
          <Outlet />
        </main>
      </div>

      <footer className="border-t border-border/60 bg-muted/20">
        <div className="mx-auto flex w-full max-w-7xl flex-col items-start justify-between gap-3 px-4 py-6 text-xs text-muted-foreground md:flex-row md:items-center md:px-6">
          <p>&copy; {new Date().getFullYear()} Reflex. Built on Runloop.</p>
          <div className="flex items-center gap-4">
            <NavLink to="/pages" className="hover:text-foreground">
              Overview
            </NavLink>
            <a
              href={EXTERNAL_LINKS.status}
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-foreground"
            >
              Status
            </a>
            <NavLink to="/" className="hover:text-foreground">
              Sign in
            </NavLink>
          </div>
        </div>
      </footer>
    </div>
  );
}
